import api from "./axiosConfig";

export interface TenantSettingsDto {
  companyName: string;
  businessEmail: string;
  businessPhone: string;
  businessStreet: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
  websiteUrl: string;
  subdomain: string;
  portalSubdomainDisplay: string;
  subscriptionTier: string;
  customDomain: string;
  customDomainStatus: string;
  customDomainEditable: boolean;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  accentColor: string;
  tagline: string;
  portalWelcomeMessage: string;
  chatbotName: string;
  chatbotWelcomeMessage: string;
  emailFromName: string;
  replyToEmail: string;
  emailFooter: string;
  emailProviderStatus: string;
  notifyEmailDisputeUpdates: boolean;
  notifyEmailNewClientSignups: boolean;
  notifyEmailBillingAlerts: boolean;
  notifyInAppDisputeUpdates: boolean;
  notifyInAppNewClientSignups: boolean;
  notifyInAppBillingAlerts: boolean;
  notifyClientDisputeStatus: boolean;
  notifyClientScoreUpdates: boolean;
  clientSelfSignup: boolean;
  requireDocumentsOnSignup: boolean;
  defaultPortalLanguage: string;
  aiChatbotEnabled: boolean;
  operatorSignerLegalName: string;
  operatorSignerTitle: string;
}

export type UpdateTenantSettingsRequest = Partial<{
  companyName: string;
  businessEmail: string;
  businessPhone: string;
  businessStreet: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
  websiteUrl: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  accentColor: string;
  tagline: string;
  portalWelcomeMessage: string;
  chatbotName: string;
  chatbotWelcomeMessage: string;
  customDomain: string;
  emailFromName: string;
  replyToEmail: string;
  emailFooter: string;
  notifyEmailDisputeUpdates: boolean;
  notifyEmailNewClientSignups: boolean;
  notifyEmailBillingAlerts: boolean;
  notifyInAppDisputeUpdates: boolean;
  notifyInAppNewClientSignups: boolean;
  notifyInAppBillingAlerts: boolean;
  notifyClientDisputeStatus: boolean;
  notifyClientScoreUpdates: boolean;
  clientSelfSignup: boolean;
  requireDocumentsOnSignup: boolean;
  defaultPortalLanguage: string;
  aiChatbotEnabled: boolean;
}>;

export const settingsApi = {
  get: async (): Promise<TenantSettingsDto> => {
    const { data } = await api.get<TenantSettingsDto>("/settings");
    return data;
  },

  update: async (body: UpdateTenantSettingsRequest): Promise<TenantSettingsDto> => {
    const { data } = await api.put<TenantSettingsDto>("/settings", body);
    return data;
  },

  uploadLogo: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post<{ logoUrl: string }>("/settings/logo", form);
    return data.logoUrl;
  },

  uploadFavicon: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post<{ faviconUrl: string }>("/settings/favicon", form);
    return data.faviconUrl;
  },

  exportJson: async (): Promise<Blob> => {
    const { data } = await api.get<Blob>("/settings/export", { responseType: "blob" });
    return data;
  },

  requestDelete: async (confirmPhrase: string): Promise<void> => {
    await api.post("/settings/delete-request", { confirmPhrase });
  },

  /**
   * Phase 7.5 — sets operator counter-signer identity. Admin-only on the backend (TenantsController
   * is `[Authorize(Roles = "Admin")]`). The read path is `settingsApi.get()` which already exposes
   * `operatorSignerLegalName` / `operatorSignerTitle`.
   */
  updateContractSigner: async (
    operatorSignerLegalName: string,
    operatorSignerTitle: string
  ): Promise<{ operatorSignerLegalName: string; operatorSignerTitle: string }> => {
    const { data } = await api.put<{
      operatorSignerLegalName: string;
      operatorSignerTitle: string;
    }>("/tenants/me/contract-signer", { operatorSignerLegalName, operatorSignerTitle });
    return data;
  },
};
